// Local-only persistence (Phase 6). IndexedDB wrapper added in Phase 6. Stores
// saved trails, explored-source history (the "N sources explored" headline
// count), source desk/bookmark identity, and prefs.

import { get, set } from "idb-keyval";
import { isSourceDisplayMode, type SourceDisplayMode } from "./sourceLang.ts";

export interface Prefs {
  theme: "light" | "dark";
  lang: "he" | "en" | "both";
  // The "Show sources" control: how source LISTS display/filter by language
  // (default / english / hebrew). Distinct from `lang`, which is reserved for
  // the reader's translation display (currently unwired).
  sourceLang: SourceDisplayMode;
  // Desktop "Your trail" rail open state: true (default) = rail shown; false = collapsed.
  railPinned: boolean;
  // Reader display, persisted so the study choice survives reloads. textScale is the
  // --reader-scale multiplier (clamped 0.85..1.45 on read); showTranslation is whether
  // the opened text's English shows (Hebrew is always the hero, so only En toggles).
  textScale: number;
  showTranslation: boolean;
}

export const DEFAULT_PREFS: Prefs = {
  theme: "light",
  lang: "both",
  sourceLang: "default",
  railPinned: true,
  textScale: 1,
  showTranslation: true,
};

// IndexedDB keys (one namespace, idb-keyval's default store).
const K_PREFS = "prefs";
const K_DESK = "desk";
const K_EXPLORED = "explored";
const K_TRAILS = "trails";
const K_BOOKMARKS = "bookmarks";

export interface StoredDeskItem {
  ref: string;
  enTitle: string;
  heTitle: string | null;
  category: string;
  addedAt: number;
}

export interface SavedTrail {
  name: string;
  data: unknown;
}

export interface BookmarkItem {
  ref: string;
  enTitle: string;
  heTitle: string | null;
  category: string;
  addedAt: number;
}

// One-time per-session persistence request -> ask the browser to keep our data
// (best-effort; everything guarded since these APIs may be missing in tests).
let persistRequested = false;
async function ensurePersisted(): Promise<void> {
  if (persistRequested) return;
  persistRequested = true;
  // Best-effort: ask the browser to keep our IndexedDB data. The boolean result is
  // advisory (some browsers auto-grant, some never prompt), so we don't act on it.
  await navigator.storage?.persist?.();
}

// Read a key, falling back to `fallback` on missing/undefined (never throws).
async function read<T>(key: string, fallback: T): Promise<T> {
  try {
    const v = await get<T>(key);
    return v === undefined ? fallback : v;
  } catch {
    return fallback;
  }
}

// Write path -> fire the one-time persist request (best-effort, NOT awaited: a
// slow or rejecting persist() must never delay or drop the write), then store.
async function write(key: string, value: unknown): Promise<void> {
  void ensurePersisted().catch(() => {});
  await set(key, value);
}

const mutationQueues = new Map<string, Promise<void>>();

function queueMutation<T>(key: string, task: () => Promise<T>): Promise<T> {
  const prev = mutationQueues.get(key) ?? Promise.resolve();
  const run = prev.catch(() => undefined).then(task);
  const settled = run.then(
    () => undefined,
    () => undefined,
  );
  mutationQueues.set(key, settled);
  void settled.then(() => {
    if (mutationQueues.get(key) === settled) mutationQueues.delete(key);
  });
  return run;
}

// Shape guards so corrupted stored / imported data can't silently poison logic
// (a malformed stored/imported object must never write a logic-breaking shape).
function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}
// Stored prefs may predate `sourceLang` (added 2026-06) or `railPinned`
// (added v5): a legacy object must stay valid (it must NOT reset the user's
// theme), so the guard checks the original fields and getPrefs normalizes
// the newer ones.
type StoredPrefs = Omit<Prefs, "sourceLang" | "railPinned" | "textScale" | "showTranslation"> & {
  sourceLang?: unknown;
  railPinned?: unknown;
  textScale?: unknown;
  showTranslation?: unknown;
};
function isValidPrefs(p: unknown): p is StoredPrefs {
  return (
    isRecord(p) &&
    (p.theme === "light" || p.theme === "dark") &&
    (p.lang === "he" || p.lang === "en" || p.lang === "both")
  );
}
function isValidStoredDeskItem(v: unknown): v is StoredDeskItem {
  return (
    isRecord(v) &&
    typeof v.ref === "string" &&
    typeof v.enTitle === "string" &&
    (typeof v.heTitle === "string" || v.heTitle === null) &&
    typeof v.category === "string" &&
    Number.isFinite(v.addedAt)
  );
}
function isValidBookmarkItem(v: unknown): v is BookmarkItem {
  return (
    isRecord(v) &&
    typeof v.ref === "string" &&
    typeof v.enTitle === "string" &&
    (typeof v.heTitle === "string" || v.heTitle === null) &&
    typeof v.category === "string" &&
    Number.isFinite(v.addedAt)
  );
}

function cleanDeskItems(items: unknown): StoredDeskItem[] {
  return Array.isArray(items) ? items.filter(isValidStoredDeskItem).map(cleanDeskItem) : [];
}
function cleanDeskItem(item: StoredDeskItem): StoredDeskItem {
  return {
    ref: item.ref,
    enTitle: item.enTitle,
    heTitle: item.heTitle,
    category: item.category,
    addedAt: item.addedAt,
  };
}
function cleanBookmarkItems(items: unknown): BookmarkItem[] {
  return Array.isArray(items) ? items.filter(isValidBookmarkItem).map(cleanBookmarkItem) : [];
}
function cleanBookmarkItem(item: BookmarkItem): BookmarkItem {
  return {
    ref: item.ref,
    enTitle: item.enTitle,
    heTitle: item.heTitle,
    category: item.category,
    addedAt: item.addedAt,
  };
}

export async function getPrefs(): Promise<Prefs> {
  const p = await read<unknown>(K_PREFS, DEFAULT_PREFS);
  if (!isValidPrefs(p)) return DEFAULT_PREFS;
  return {
    theme: p.theme,
    lang: p.lang,
    sourceLang: isSourceDisplayMode(p.sourceLang) ? p.sourceLang : "default",
    railPinned: p.railPinned !== false, // default open; only a literal false collapses
    // clamp to the reader stepper's range; anything out of range / non-number -> 1
    textScale: typeof p.textScale === "number" && p.textScale >= 0.85 && p.textScale <= 1.45 ? p.textScale : 1,
    showTranslation: p.showTranslation !== false, // default shown; only a literal false hides
  };
}

export async function setPrefs(p: Prefs): Promise<void> {
  await write(K_PREFS, p);
}

// Merge a partial update into the stored prefs. Serialized through the K_PREFS
// queue so two quick changes (theme + text scale, repeated A+) each read the
// other's result instead of overwriting it with stale fields. Rejects if the
// write fails; callers decide whether to swallow.
export async function updatePrefs(patch: Partial<Prefs>): Promise<void> {
  await queueMutation(K_PREFS, async () => {
    const prefs = await getPrefs();
    await setPrefs({ ...prefs, ...patch });
  });
}

// Persist the desktop "Your trail" rail open/collapsed state (railPinned).
export async function persistRailOpen(open: boolean): Promise<void> {
  try {
    await updatePrefs({ railPinned: open });
  } catch {
    /* ignore persistence failure - rail state still applied this session */
  }
}

// Explored count -> monotonic set of unique refs; each ref counts once.
export async function markExplored(ref: string): Promise<void> {
  await queueMutation(K_EXPLORED, async () => {
    const refs = await read<string[]>(K_EXPLORED, []);
    if (refs.includes(ref)) return;
    refs.push(ref);
    await write(K_EXPLORED, refs);
  });
}

export async function exploredCount(): Promise<number> {
  return (await read<string[]>(K_EXPLORED, [])).length;
}

export async function listExplored(): Promise<string[]> {
  return read<string[]>(K_EXPLORED, []);
}

// Per-pasuk footprints (Tranche 2): the verses whose SOURCES you opened, any
// session. Quiet memory, not completion - rendered as a tiny dot, never a
// percentage. Separate from K_EXPLORED (that one counts SOURCES and feeds the
// honest "N sources explored" headline; this one marks PESUKIM).
const K_VERSEVISITED = "verseVisited";
export async function markVerseVisited(ref: string): Promise<void> {
  await queueMutation(K_VERSEVISITED, async () => {
    const refs = await read<string[]>(K_VERSEVISITED, []);
    if (refs.includes(ref)) return;
    refs.push(ref);
    await write(K_VERSEVISITED, refs);
  });
}
export async function listVerseVisited(): Promise<string[]> {
  return read<string[]>(K_VERSEVISITED, []);
}

export async function isExplored(ref: string): Promise<boolean> {
  return (await read<string[]>(K_EXPLORED, [])).includes(ref);
}

// Saved trails -> append-style list (caller owns the opaque `data` shape).
export async function saveTrail(name: string, data: unknown): Promise<void> {
  await queueMutation(K_TRAILS, async () => {
    const trails = await read<SavedTrail[]>(K_TRAILS, []);
    trails.push({ name, data });
    await write(K_TRAILS, trails);
  });
}

export async function listTrails(): Promise<SavedTrail[]> {
  return read<SavedTrail[]>(K_TRAILS, []);
}

export async function getDesk(): Promise<StoredDeskItem[]> {
  return cleanDeskItems(await read<unknown>(K_DESK, []));
}

export async function setDesk(items: StoredDeskItem[]): Promise<void> {
  await write(K_DESK, cleanDeskItems(items));
}

export async function getBookmarks(): Promise<BookmarkItem[]> {
  return cleanBookmarkItems(await read<unknown>(K_BOOKMARKS, []));
}

export async function addBookmark(item: BookmarkItem): Promise<"added" | "exists"> {
  return queueMutation(K_BOOKMARKS, async () => {
    const bookmarks = await getBookmarks();
    if (bookmarks.some((b) => b.ref === item.ref)) return "exists";
    bookmarks.push(cleanBookmarkItem(item));
    await write(K_BOOKMARKS, bookmarks);
    return "added";
  });
}

export async function removeBookmark(ref: string): Promise<void> {
  await queueMutation(K_BOOKMARKS, async () => {
    const bookmarks = await getBookmarks();
    await write(K_BOOKMARKS, bookmarks.filter((b) => b.ref !== ref));
  });
}

// Snapshot shape for backup ("Back up my progress").
export interface Snapshot {
  prefs: Prefs;
  explored: string[];
  trails: SavedTrail[];
  versesVisited?: string[]; // added Tranche 2; absent in older backups (tolerated)
  desk?: StoredDeskItem[];
  bookmarks?: BookmarkItem[];
}

export async function exportAll(): Promise<string> {
  const explored = await read<string[]>(K_EXPLORED, []);
  const snap: Snapshot = {
    prefs: await getPrefs(),
    explored,
    trails: await listTrails(),
    versesVisited: await listVerseVisited(),
    desk: await getDesk(),
    bookmarks: await getBookmarks(),
  };
  return JSON.stringify(snap);
}

export async function importAll(json: string): Promise<void> {
  let snap: Partial<Snapshot>;
  try {
    snap = JSON.parse(json) as Partial<Snapshot>;
  } catch {
    return; // tolerate garbage input -> no-op
  }
  // validate on import so a hand-edited backup can't write a logic-breaking shape
  if (isValidPrefs(snap.prefs)) await write(K_PREFS, snap.prefs);
  if (Array.isArray(snap.explored)) await write(K_EXPLORED, snap.explored);
  if (Array.isArray(snap.trails)) await write(K_TRAILS, snap.trails);
  if (Array.isArray(snap.versesVisited)) await write(K_VERSEVISITED, snap.versesVisited);
  if (Array.isArray(snap.desk)) await write(K_DESK, cleanDeskItems(snap.desk));
  if (Array.isArray(snap.bookmarks)) await write(K_BOOKMARKS, cleanBookmarkItems(snap.bookmarks));
  // Older backups may carry a "reviews" field from the removed SM-2 layer; it is
  // simply ignored on import.
}
